import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Room, RoomEvent, Track } from 'livekit-client';
import { supabase } from '../lib/supabaseClient';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';
const ICE_SERVERS = [{ urls: 'stun:stun.l.google.com:19302' }];

export default function useChatCalls({ user, conversations = [] }) {
  const [incomingCall, setIncomingCall] = useState(null);
  const [activeCall, setActiveCall] = useState(null);
  const [callError, setCallError] = useState('');
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOn, setIsCameraOn] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [screenStream, setScreenStream] = useState(null);
  const channelsRef = useRef(new Map());
  const conversationsRef = useRef(conversations);
  const userRef = useRef(user);
  const activeCallRef = useRef(null);
  const incomingCallRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const offerInProgressRef = useRef(false);
  const createOfferRef = useRef(null);
  const pendingIceCandidatesRef = useRef([]);
  const localStreamRef = useRef(null);
  const screenTrackRef = useRef(null);
  const liveKitRoomRef = useRef(null);
  const liveKitAudioElementsRef = useRef(new Set());
  const remoteVideoTracksRef = useRef(new Map());

  conversationsRef.current = conversations;
  userRef.current = user;
  activeCallRef.current = activeCall;
  incomingCallRef.current = incomingCall;

  const conversationKey = useMemo(
    () => conversations
      .filter((conversation) => ['direct', 'group'].includes(conversation.kind))
      .map((conversation) => conversation.id)
      .sort()
      .join(','),
    [conversations]
  );

  const updateActiveCall = useCallback((call) => {
    activeCallRef.current = call;
    setActiveCall(call);
  }, []);

  const updateIncomingCall = useCallback((call) => {
    incomingCallRef.current = call;
    setIncomingCall(call);
  }, []);

  const publish = useCallback(async (conversationId, event, payload) => {
    const channel = channelsRef.current.get(conversationId);
    if (!channel || channel.state !== 'joined') {
      throw new Error('Lidhja e thirrjes nuk është gati. Provo përsëri.');
    }
    const status = await channel.send({
      type: 'broadcast',
      event,
      payload,
    });
    if (status !== 'ok') {
      throw new Error('Sinjalizimi i thirrjes dështoi.');
    }
  }, []);

  const releaseTransport = useCallback(() => {
    const peerConnection = peerConnectionRef.current;
    if (peerConnection) {
      peerConnection.onicecandidate = null;
      peerConnection.ontrack = null;
      peerConnection.onnegotiationneeded = null;
      peerConnection.close();
      peerConnectionRef.current = null;
    }
    offerInProgressRef.current = false;
    pendingIceCandidatesRef.current = [];

    const room = liveKitRoomRef.current;
    liveKitRoomRef.current = null;
    if (room) void room.disconnect();
    liveKitAudioElementsRef.current.forEach((element) => element.remove());
    liveKitAudioElementsRef.current.clear();
    remoteVideoTracksRef.current.clear();

    localStreamRef.current?.getTracks().forEach((track) => track.stop());
    localStreamRef.current = null;
    screenTrackRef.current = null;
    setScreenStream(null);
    setIsScreenSharing(false);
    setLocalStream(null);
    setRemoteStream(null);
    setIsMuted(false);
    setIsCameraOn(false);
  }, []);

  const createPeerConnection = useCallback((call) => {
    if (peerConnectionRef.current) return peerConnectionRef.current;
    const peerConnection = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    peerConnectionRef.current = peerConnection;

    (localStreamRef.current?.getTracks() || []).forEach((track) => {
      peerConnection.addTrack(track, localStreamRef.current);
    });

    peerConnection.onicecandidate = (event) => {
      if (!event.candidate) return;
      void publish(call.conversationId, 'call-signal', {
        callId: call.callId,
        fromUid: userRef.current?.id,
        signalType: 'ice',
        candidate: event.candidate,
      }).catch((error) => {
        console.error('Unable to send call ICE candidate:', error);
        setCallError(error.message);
      });
    };
    peerConnection.ontrack = (event) => {
      const stream = event.streams?.[0] || new MediaStream([event.track]);
      setRemoteStream(stream);
    };
    peerConnection.onnegotiationneeded = () => {
      const active = activeCallRef.current;
      if (!active?.accepted || active.isGroup) return;
      void createOfferRef.current?.(active).catch((error) => {
        console.error('Unable to renegotiate call media:', error);
        setCallError('Nuk u arrit të përditësohet media e thirrjes.');
      });
    };
    return peerConnection;
  }, [publish]);

  const createAndSendOffer = useCallback(async (call) => {
    const peerConnection = createPeerConnection(call);
    if (peerConnection.signalingState !== 'stable' || offerInProgressRef.current) return;
    offerInProgressRef.current = true;
    try {
      const offer = await peerConnection.createOffer();
      await peerConnection.setLocalDescription(offer);
      await publish(call.conversationId, 'call-signal', {
        callId: call.callId,
        fromUid: userRef.current?.id,
        signalType: 'offer',
        sdp: peerConnection.localDescription,
      });
    } finally {
      offerInProgressRef.current = false;
    }
  }, [createPeerConnection, publish]);
  createOfferRef.current = createAndSendOffer;

  const connectGroupRoom = useCallback(async (call, stream) => {
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError) throw sessionError;
    if (!session?.access_token) throw new Error('Hyr përsëri për të nisur thirrjen.');

    const response = await fetch(`${API_BASE_URL}/api/calls/livekit/group-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        conversationId: call.conversationId,
        displayName: call.fromName || userRef.current?.firstName || 'Student',
      }),
    });
    const result = await response.json();
    if (!response.ok || !result?.success || !result?.data?.token || !result?.data?.url) {
      throw new Error(result?.detail || 'Nuk u lidh thirrja në grup.');
    }

    const room = new Room({ adaptiveStream: true, dynacast: true });
    liveKitRoomRef.current = room;
    room.on(RoomEvent.TrackSubscribed, (track) => {
      if (track.kind === Track.Kind.Audio) {
        const audioElement = track.attach();
        audioElement.autoplay = true;
        audioElement.setAttribute('playsinline', '');
        document.body.appendChild(audioElement);
        liveKitAudioElementsRef.current.add(audioElement);
      } else if (track.kind === Track.Kind.Video) {
        remoteVideoTracksRef.current.set(track.sid, track.mediaStreamTrack);
        setRemoteStream(new MediaStream(Array.from(remoteVideoTracksRef.current.values())));
      }
    });
    room.on(RoomEvent.TrackUnsubscribed, (track) => {
      track.detach().forEach((element) => {
        liveKitAudioElementsRef.current.delete(element);
        element.remove();
      });
      remoteVideoTracksRef.current.delete(track.sid);
      setRemoteStream(
        remoteVideoTracksRef.current.size
          ? new MediaStream(Array.from(remoteVideoTracksRef.current.values()))
          : null
      );
    });
    room.on(RoomEvent.Disconnected, () => {
      liveKitAudioElementsRef.current.forEach((element) => element.remove());
      liveKitAudioElementsRef.current.clear();
      remoteVideoTracksRef.current.clear();
      setRemoteStream(null);
    });

    await room.connect(result.data.url, result.data.token);
    const audioTrack = stream?.getAudioTracks?.()[0];
    if (audioTrack) {
      await room.localParticipant.publishTrack(audioTrack, { source: Track.Source.Microphone });
    } else {
      await room.localParticipant.setMicrophoneEnabled(true);
    }
    const videoTrack = stream?.getVideoTracks?.()[0];
    if (videoTrack) {
      await room.localParticipant.publishTrack(videoTrack, { source: Track.Source.Camera });
    }
  }, []);

  const startCall = useCallback(async (conversation) => {
    if (!conversation?.id || !userRef.current?.id) return;
    if (activeCallRef.current || incomingCallRef.current) {
      setCallError('Përfundo thirrjen aktuale para se të nisësh një tjetër.');
      return;
    }
    setCallError('');

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStreamRef.current = stream;
      setLocalStream(stream);
      const call = {
        callId: `${userRef.current.id}-${conversation.id}-${Date.now()}`,
        conversationId: conversation.id,
        conversation,
        fromUid: userRef.current.id,
        fromName: [userRef.current.firstName, userRef.current.lastName].filter(Boolean).join(' ')
          || userRef.current.username
          || 'Student',
        isGroup: conversation.kind === 'group',
        role: 'caller',
      };
      updateActiveCall(call);
      await publish(conversation.id, 'call-status', {
        ...call,
        status: 'calling',
      });

      if (call.isGroup) {
        await connectGroupRoom(call, stream);
      }
    } catch (error) {
      console.error('Unable to start call:', error);
      releaseTransport();
      updateActiveCall(null);
      setCallError(error.message || 'Nuk u arrit të niset thirrja.');
    }
  }, [connectGroupRoom, publish, releaseTransport, updateActiveCall]);

  const acceptIncomingCall = useCallback(async () => {
    const call = incomingCallRef.current;
    if (!call || !userRef.current?.id) return;
    setCallError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStreamRef.current = stream;
      setLocalStream(stream);
      const active = {
        ...call,
        role: 'callee',
      };
      updateActiveCall(active);
      updateIncomingCall(null);
      await publish(call.conversationId, 'call-status', {
        callId: call.callId,
        conversationId: call.conversationId,
        fromUid: userRef.current.id,
        fromName: [userRef.current.firstName, userRef.current.lastName].filter(Boolean).join(' ')
          || userRef.current.username
          || 'Student',
        isGroup: call.isGroup,
        status: 'accepted',
      });
      if (call.isGroup) {
        await connectGroupRoom(active, stream);
      }
    } catch (error) {
      console.error('Unable to accept call:', error);
      releaseTransport();
      updateActiveCall(null);
      setCallError(error.message || 'Nuk u arrit të pranohen thirrja.');
    }
  }, [connectGroupRoom, publish, releaseTransport, updateActiveCall, updateIncomingCall]);

  const rejectIncomingCall = useCallback(async () => {
    const call = incomingCallRef.current;
    if (!call) return;
    try {
      await publish(call.conversationId, 'call-status', {
        callId: call.callId,
        conversationId: call.conversationId,
        fromUid: userRef.current?.id,
        isGroup: call.isGroup,
        status: 'declined',
      });
    } catch (error) {
      console.error('Unable to reject call:', error);
      setCallError(error.message || 'Nuk u arrit të refuzohet thirrja.');
    } finally {
      updateIncomingCall(null);
    }
  }, [publish, updateIncomingCall]);

  const endCall = useCallback(async () => {
    const call = activeCallRef.current;
    if (call) {
      try {
        await publish(call.conversationId, 'call-status', {
          callId: call.callId,
          conversationId: call.conversationId,
          fromUid: userRef.current?.id,
          isGroup: call.isGroup,
          status: call.isGroup ? 'left' : 'ended',
        });
      } catch (error) {
        console.error('Unable to end call cleanly:', error);
      }
    }
    releaseTransport();
    updateActiveCall(null);
  }, [publish, releaseTransport, updateActiveCall]);

  const toggleMute = useCallback(async () => {
    const nextMuted = !isMuted;
    localStreamRef.current?.getAudioTracks().forEach((track) => {
      track.enabled = !nextMuted;
    });
    const room = liveKitRoomRef.current;
    try {
      if (room) {
        await room.localParticipant.setMicrophoneEnabled(!nextMuted);
      }
      setIsMuted(nextMuted);
    } catch (error) {
      console.error('Unable to change microphone state:', error);
      setCallError('Nuk u arrit të ndryshohet mikrofoni.');
    }
  }, [isMuted]);

  const toggleCamera = useCallback(async () => {
    const call = activeCallRef.current;
    if (!call) return;
    const nextCameraOn = !isCameraOn;
    try {
      if (call.isGroup && liveKitRoomRef.current) {
        const participant = liveKitRoomRef.current.localParticipant;
        await participant.setCameraEnabled(nextCameraOn);
        const cameraPublication = Array.from(participant.videoTrackPublications.values())
          .find((publication) => publication.source === Track.Source.Camera);
        const cameraTrack = cameraPublication?.track?.mediaStreamTrack;
        const stream = localStreamRef.current || new MediaStream();
        if (nextCameraOn && cameraTrack && !stream.getTracks().some((track) => track.id === cameraTrack.id)) {
          stream.addTrack(cameraTrack);
        } else if (!nextCameraOn) {
          stream.getVideoTracks().forEach((track) => stream.removeTrack(track));
        }
        localStreamRef.current = stream;
        setLocalStream(new MediaStream(stream.getTracks()));
        setIsCameraOn(nextCameraOn);
        return;
      }

      const peerConnection = peerConnectionRef.current;
      if (nextCameraOn) {
        const cameraStream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
        const cameraTrack = cameraStream.getVideoTracks()[0];
        const currentStream = localStreamRef.current || new MediaStream();
        currentStream.addTrack(cameraTrack);
        localStreamRef.current = currentStream;
        setLocalStream(new MediaStream(currentStream.getTracks()));
        if (peerConnection) {
          peerConnection.addTrack(cameraTrack, currentStream);
          if (call.role === 'caller' && peerConnection.signalingState === 'stable') {
            await createAndSendOffer(call);
          }
        }
      } else {
        const stream = localStreamRef.current;
        stream?.getVideoTracks().forEach((track) => {
          peerConnection?.getSenders().find((sender) => sender.track?.id === track.id)?.replaceTrack(null);
          stream.removeTrack(track);
          track.stop();
        });
        setLocalStream(stream ? new MediaStream(stream.getTracks()) : null);
      }
      setIsCameraOn(nextCameraOn);
    } catch (error) {
      console.error('Unable to change camera state:', error);
      setCallError(error.message || 'Nuk u arrit të ndryshohet kamera.');
    }
  }, [createAndSendOffer, isCameraOn]);

  const toggleScreenShare = useCallback(async () => {
    const call = activeCallRef.current;
    if (!call) return;
    const nextSharing = !isScreenSharing;
    try {
      if (call.isGroup && liveKitRoomRef.current) {
        const participant = liveKitRoomRef.current.localParticipant;
        await participant.setScreenShareEnabled(nextSharing);
        const publication = Array.from(participant.videoTrackPublications.values())
          .find((item) => item.source === Track.Source.ScreenShare);
        const track = publication?.track?.mediaStreamTrack;
        const local = localStreamRef.current || new MediaStream();
        if (nextSharing && track && !local.getTracks().some((item) => item.id === track.id)) {
          local.addTrack(track);
          screenTrackRef.current = track;
        } else if (!nextSharing) {
          if (screenTrackRef.current) local.removeTrack(screenTrackRef.current);
          screenTrackRef.current = null;
        }
        localStreamRef.current = local;
        setLocalStream(new MediaStream(local.getTracks()));
        setScreenStream(nextSharing && track ? new MediaStream([track]) : null);
        setIsScreenSharing(nextSharing);
        return;
      }

      if (nextSharing) {
        const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
        const track = stream.getVideoTracks()[0];
        const local = localStreamRef.current || new MediaStream();
        local.addTrack(track);
        screenTrackRef.current = track;
        localStreamRef.current = local;
        setLocalStream(new MediaStream(local.getTracks()));
        setScreenStream(stream);
        setIsScreenSharing(true);
        const peerConnection = peerConnectionRef.current;
        if (peerConnection) peerConnection.addTrack(track, local);
        track.onended = () => {
          local.removeTrack(track);
          screenTrackRef.current = null;
          setLocalStream(new MediaStream(local.getTracks()));
          setScreenStream(null);
          setIsScreenSharing(false);
        };
      } else {
        const track = screenTrackRef.current || screenStream?.getVideoTracks()[0];
        if (track) {
          const peerConnection = peerConnectionRef.current;
          const sender = peerConnection?.getSenders().find((item) => item.track?.id === track.id);
          if (sender) peerConnection.removeTrack(sender);
          localStreamRef.current?.removeTrack(track);
          track.stop();
        }
        screenTrackRef.current = null;
        setLocalStream(
          localStreamRef.current ? new MediaStream(localStreamRef.current.getTracks()) : null
        );
        setScreenStream(null);
        setIsScreenSharing(false);
      }
    } catch (error) {
      console.error('Unable to share screen in call:', error);
      setCallError(error.message || 'Nuk u arrit të ndahet ekrani.');
    }
  }, [isScreenSharing, screenStream]);

  const handleCallSignal = useCallback(async (payload) => {
    const call = activeCallRef.current;
    if (!call || call.isGroup || payload?.callId !== call.callId) return;
    try {
      if (payload.signalType === 'offer' && payload.sdp) {
        const acceptedCall = { ...call, accepted: true };
        updateActiveCall(acceptedCall);
        const peerConnection = createPeerConnection(acceptedCall);
        if (peerConnection.signalingState === 'have-local-offer') {
          await peerConnection.setLocalDescription({ type: 'rollback' });
        }
        await peerConnection.setRemoteDescription(payload.sdp);
        for (const candidate of pendingIceCandidatesRef.current.splice(0)) {
          await peerConnection.addIceCandidate(candidate);
        }
        const answer = await peerConnection.createAnswer();
        await peerConnection.setLocalDescription(answer);
        await publish(call.conversationId, 'call-signal', {
          callId: call.callId,
          fromUid: userRef.current?.id,
          signalType: 'answer',
          sdp: peerConnection.localDescription,
        });
      } else if (payload.signalType === 'answer' && payload.sdp) {
        const peerConnection = peerConnectionRef.current;
        if (!peerConnection) return;
        await peerConnection.setRemoteDescription(payload.sdp);
        for (const candidate of pendingIceCandidatesRef.current.splice(0)) {
          await peerConnection.addIceCandidate(candidate);
        }
      } else if (payload.signalType === 'ice' && payload.candidate) {
        const peerConnection = peerConnectionRef.current;
        if (!peerConnection?.remoteDescription) {
          pendingIceCandidatesRef.current.push(payload.candidate);
        } else {
          await peerConnection.addIceCandidate(payload.candidate);
        }
      }
    } catch (error) {
      console.error('Call negotiation failed:', error);
      setCallError('Lidhja e thirrjes u ndërpre. Provo ta nisësh përsëri.');
    }
  }, [createPeerConnection, publish, updateActiveCall]);

  const handleCallStatus = useCallback((payload) => {
    const currentUser = userRef.current;
    if (!payload || !currentUser?.id) return;
    const call = activeCallRef.current;
    if (payload.status === 'calling' && payload.fromUid !== currentUser.id) {
      const conversation = conversationsRef.current.find(
        (item) => item.id === payload.conversationId
      );
      if (!conversation || call || incomingCallRef.current) return;
      updateIncomingCall({
        ...payload,
        conversation,
        peerName: payload.fromName || conversation.title,
      });
      return;
    }
    if (!call || payload.callId !== call.callId || payload.fromUid === currentUser.id) return;
    if (payload.status === 'accepted' && !call.isGroup && call.role === 'caller') {
      updateActiveCall({ ...call, accepted: true });
      void createAndSendOffer(call).catch((error) => {
        console.error('Unable to start WebRTC offer:', error);
        setCallError('Nuk u arrit të lidhet me përdoruesin.');
      });
    } else if (payload.status === 'declined' && !call.isGroup) {
      releaseTransport();
      updateActiveCall(null);
      setCallError('Thirrja u refuzua.');
    } else if (payload.status === 'ended' && !call.isGroup) {
      releaseTransport();
      updateActiveCall(null);
    }
  }, [createAndSendOffer, releaseTransport, updateActiveCall, updateIncomingCall]);

  useEffect(() => {
    if (!user?.id) return undefined;
    const ids = conversationKey ? conversationKey.split(',') : [];
    const channelsByConversation = channelsRef.current;
    const channels = ids.map((conversationId) => {
      const channel = supabase.channel(`chat-call:${conversationId}`, {
        config: { private: true, broadcast: { self: false } },
      });
      channel
        .on('broadcast', { event: 'call-status' }, ({ payload }) => handleCallStatus(payload))
        .on('broadcast', { event: 'call-signal' }, ({ payload }) => {
          void handleCallSignal(payload);
        })
        .subscribe((status, error) => {
          if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            console.error('Unable to subscribe to private call channel:', error || status);
          }
        });
      channelsByConversation.set(conversationId, channel);
      return channel;
    });

    return () => {
      channelsByConversation.clear();
      channels.forEach((channel) => {
        void supabase.removeChannel(channel);
      });
    };
  }, [user?.id, conversationKey, handleCallSignal, handleCallStatus]);

  useEffect(() => () => {
    releaseTransport();
  }, [releaseTransport]);

  return {
    incomingCall,
    activeCall,
    callError,
    localStream,
    remoteStream,
    isMuted,
    isCameraOn,
    isScreenSharing,
    screenStream,
    startCall,
    acceptIncomingCall,
    rejectIncomingCall,
    endCall,
    toggleMute,
    toggleCamera,
    toggleScreenShare,
    dismissCallError: () => setCallError(''),
  };
}
